import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useCategoryBootstrap } from '../hooks/useCategoryBootstrap';
import { CategoryHelpers } from '../types/stockCategories';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AlertCircle, ArrowLeft, Calendar, Eye } from 'lucide-react';
import FinancialMetrics from '../components/game/FinancialMetrics';
import StockChart from '../components/game/StockChart';

/**
 * YesterdayPage - Read-only page showing yesterday's WallStreetBets solution
 *
 * This is a "secret" page (not linked in navigation) that displays yesterday's
 * stock of the day with all hints revealed and no gameplay interaction.
 *
 * URL: /wallstreetbets/yesterday
 */
export const YesterdayPage: React.FC = () => {
  const navigate = useNavigate();
  const category = 'wallstreetbets';

  // Calculate yesterday's date in UTC
  const yesterday = useMemo(() => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - 1);
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  }, []);

  const categoryConfig = CategoryHelpers.getConfig(category);

  // Bootstrap with yesterday's date and read-only flag
  const bootstrap = useCategoryBootstrap({
    category,
    date: yesterday,
    isReadOnly: true, // Skip "already played" check
    disabled: false
  });

  // Show loading state
  if (!bootstrap.bootstrapState.isPageReady) {
    if (bootstrap.bootstrapState.error) {
      return (
        <>
          <Helmet>
            <title>Wallstreetle | Yesterday's Solution</title>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <div className="min-h-screen flex items-center justify-center p-4 bg-background">
            <Card className="max-w-md w-full p-6 text-center">
              <AlertCircle className="mx-auto mb-4 text-destructive" size={48} />
              <h2 className="text-lg font-semibold mb-2">
                Yesterday's Data Unavailable
              </h2>
              <p className="text-muted-foreground mb-4">
                {bootstrap.bootstrapState.error}
              </p>
              <Button onClick={() => navigate('/wallstreetbets')} className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go to Today's Game
              </Button>
            </Card>
          </div>
        </>
      );
    }

    return (
      <>
        <Helmet>
          <title>Wallstreetle | Loading Yesterday's Solution...</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold mb-2">Loading yesterday's solution...</h2>
            <p className="text-sm text-muted-foreground">
              {bootstrap.bootstrapState.steps.stock === 'loading' && 'Fetching stock data...'}
              {bootstrap.bootstrapState.steps.metrics === 'loading' && 'Loading financial metrics...'}
              {bootstrap.bootstrapState.steps.hints === 'loading' && 'Retrieving hints...'}
            </p>
          </div>
        </div>
      </>
    );
  }

  // Extract data from bootstrap
  const stockData = bootstrap.stock.stockData;
  const chartData = bootstrap.stock.chartData;
  const hints = bootstrap.hints.hints;
  const metricsMap = bootstrap.metrics.metrics as unknown as Record<string, string | null>;
  const selectedGroups = bootstrap.metrics.selectedGroups;

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Yesterday's Solution</title>
        <meta name="description" content={`View yesterday's WallStreetBets stock solution for ${yesterday}`} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header banner */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 px-4 shadow-lg">
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 flex-shrink-0" />
              <div>
                <h1 className="text-xl font-bold">Yesterday's Solution</h1>
                <p className="text-sm opacity-90">
                  {categoryConfig.label} - {yesterday}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/wallstreetbets')}
              className="bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Today's Game
            </Button>
          </div>
        </div>

        {/* Solution content */}
        <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
          {/* Stock reveal card */}
          <Card className="p-6 text-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-500/20">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-600 dark:text-green-400 uppercase tracking-wide">
                Solution
              </span>
            </div>
            <h2 className="text-4xl font-bold mb-2 text-foreground">
              {stockData?.Symbol || 'Unknown'}
            </h2>
            <p className="text-xl text-muted-foreground">
              {stockData?.Security || 'Unknown Company'}
            </p>
          </Card>

          {/* All hints revealed */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <span>All Hints</span>
              <span className="text-xs font-normal text-muted-foreground">
                (Revealed)
              </span>
            </h3>
            <div className="space-y-3">
              {hints.map((hint, idx) => (
                <div
                  key={idx}
                  className="p-4 bg-muted/50 rounded-lg border border-border"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary">
                        {idx + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm text-muted-foreground mb-1">
                        {hint.title}
                      </div>
                      <div className="text-foreground">
                        {hint.content}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Chart and metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stock chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Price Chart</h3>
              {chartData.length > 0 ? (
                <StockChart
                  data={chartData}
                  loading={bootstrap.stock.isLoading}
                />
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  Chart data unavailable
                </div>
              )}
            </Card>

            {/* Financial metrics */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Financial Metrics</h3>
              <FinancialMetrics
                metricsMap={metricsMap}
                selectedGroups={selectedGroups}
                loading={bootstrap.metrics.isLoading}
                layout="mobile-grid"
                visibleHints={4}
                category={category}
              />
            </Card>
          </div>

          {/* Back to today button (mobile friendly) */}
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => navigate('/wallstreetbets')}
              size="lg"
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Play Today's WallStreetBets Game
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default YesterdayPage;
